import './styles.scss';

import { memo, useMemo } from 'react';

import { BacktesterStrategyData } from '../../../../../providers/backtesting/api/backtester-config-api.types';
import { IPortfolioPerformanceAccountReport } from '../../../../../providers/portfolio-performance/portfolio-performance.types';
import { computeDailyStats } from '../../../../../providers/portfolio-performance/compute-daily-stats';
import { DailyReturnStatsBand } from '../../../../portfolio-performance/components/daily-return-stats-band';
import { PerformanceCard } from '../../../../portfolio-performance/components/cards/performance-card';
import { BacktestEquityChart } from './backtest-equity-chart';
import { PNLReportTable } from './pnl-report-table';

interface OverviewProps {
  report: IPortfolioPerformanceAccountReport | null;
  strategyData: BacktesterStrategyData;
  selectedStrategy: string;
}

// The "Performance" tab — the per-stratlet detail group that mirrors the PP
// account card's Performance tab (equity/underwater + daily-return stats +
// monthly-returns calendar), plus the raw PNL grid. The stratlet is chosen in
// the persistent overview table hoisted to the result shell; this component
// just renders whichever one is selected.
export const Overview = memo(({ report, strategyData, selectedStrategy }: OverviewProps) => {
  const dailyStats = useMemo(() => computeDailyStats(report?.historical), [report]);
  const executionMs = strategyData[selectedStrategy]?.execution_time_ms;

  return (
    <div className="backtest-result-view backtest-result-overview">
      {executionMs !== undefined && (
        <div className="backtest-exec-time">
          Execution time
          <span className="backtest-exec-time-value">{executionMs.toFixed(1)} ms</span>
        </div>
      )}

      <BacktestEquityChart report={report} strategyName={selectedStrategy} />

      <DailyReturnStatsBand stats={dailyStats} />

      <PerformanceCard data={report?.historical} accountName={selectedStrategy} />

      <div className="backtest-result-divider" />
      <PNLReportTable strategyData={strategyData} selectedStrategy={selectedStrategy} />
    </div>
  );
});

Overview.displayName = 'Overview';
